import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { setApiAuthToken } from '../utils/apiAuth';
import { clearLastLocation } from '../utils/lastLocation';

// Capture the URL hash BEFORE creating the supabase client. With
// detectSessionInUrl: true (default) supabase-js parses the hash
// during init and clears it from the URL, so reading window.location.hash
// after createClient() would return an empty string. We need the `type`
// parameter to know whether this session came from an invite link or a
// password-recovery link (both flows arrive as SIGNED_IN events that
// otherwise look identical to a normal login).
const initialUrlHash = typeof window !== 'undefined' ? window.location.hash : '';
const initialHashParams = new URLSearchParams(initialUrlHash.replace(/^#/, ''));
const initialAuthEventType = initialHashParams.get('type');
const initialRequiresPasswordSetup: 'invite' | 'recovery' | null =
  initialAuthEventType === 'invite' ? 'invite'
  : initialAuthEventType === 'recovery' ? 'recovery'
  : null;

// Supabase rate-limits /auth/v1/token per IP — password sign-ins AND hourly
// background refreshes share one ~30-request burst bucket, and the whole
// office NATs through a single IP. auth-js treats a 429 (or a 500) during
// refresh as FATAL: it deletes the still-valid session and signs the user
// out (only network errors and 502/503/504 are retried — see
// isAuthRetryableFetchError). One busy stretch then cascades org-wide:
// refresh 429s → forced re-login → password grants drain the same bucket →
// more 429s (the 2026-08-13..18 sign-in storm). Recast rate-limit/server
// errors on REFRESH grants only to 503 so auth-js keeps the session and
// retries on its next 30s tick. Password-grant failures pass through
// untouched — a real "invalid credentials" or rate-limit message must still
// reach the login form, and a revoked refresh token still 400s → sign-out.
const authFetch: typeof fetch = async (input, init) => {
  const res = await fetch(input, init);
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (url.includes('grant_type=refresh_token') && (res.status === 429 || res.status >= 500)) {
    return new Response(res.body, { status: 503, statusText: 'Service Unavailable', headers: res.headers });
  }
  return res;
};

// Exported so the two-factor screens can drive auth.mfa on the SAME client
// that holds the session — a second createClient would have no session to
// step up, and mfa.verify would have nothing to upgrade.
export const supabase = createClient(`https://${projectId}.supabase.co`, publicAnonKey, {
  global: { fetch: authFetch },
});

interface User {
  id: string;
  email: string;
  name?: string;
}

interface Employee {
  id: string;
  name: string;
  email: string;
  department: string;
  role: string;
  photo?: string;
  accessRole?: 'admin' | 'manager' | 'employee';
  accessTier?: 1 | 2 | 3;
  /** The access profile this person sits on — the default for every board. */
  accessProfile?: string | null;
  /** Their money rung, when it differs from the profile's. */
  moneyLensOverride?: string | null;
  employmentStatus?: 'active' | 'inactive' | 'onboarding';
}

/**
 * Where the employee lookup stands. 'unavailable' is the one that matters:
 * the server could not be asked (503, timeout, network) — it says NOTHING
 * about whether the person has a row, so `employee` keeps whatever it had.
 * 'missing' is the definitive answer: the roster came back and they are not
 * on it.
 */
export type EmployeeLookupStatus = 'pending' | 'ok' | 'missing' | 'unavailable';

interface AuthContextType {
  user: User | null;
  employee: Employee | null;
  employeeLookup: EmployeeLookupStatus;
  /** Ask the roster again — the HR module's "Try again" after 'unavailable'. */
  retryEmployeeLookup: () => Promise<void>;
  loading: boolean;
  accessToken: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  // 'invite' on first sign-in from an admin invite email; 'recovery' from
  // a password-reset link. The app should render a set-password screen
  // instead of the main UI until clearPasswordSetupRequirement() is called.
  requiresPasswordSetup: 'invite' | 'recovery' | null;
  clearPasswordSetupRequirement: () => void;
  updatePassword: (newPassword: string) => Promise<void>;
  // Two-factor. 'checking' only while the first AAL read is in flight, so the
  // app never flashes into view before we know a code is owed. Any failure
  // resolves to 'ok': a screen-level gate that can lock the whole company out
  // on a transient auth blip is a worse trade than one that opens.
  mfaState: 'checking' | 'ok' | 'challenge';
  refreshMfaState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e3a97ae5`;

// ── Employee-record SWR cache (localStorage) ─────────────────────────────────
// The auth gate used to AWAIT /hr/employees before rendering anything, so a cold
// edge isolate spun the WHOLE app (the "it just spins" symptom). We cache the
// resolved employee record (no sensitive fields — just id/name/role/tier/etc.)
// keyed by email, so a returning user's session restores instantly and we
// revalidate in the background. Cleared on signOut.
const EMP_CACHE_VERSION = 1;
const empCacheKey = (email: string | null | undefined) =>
  email ? `mos:emp:v${EMP_CACHE_VERSION}:${email.toLowerCase()}` : null;
function readEmpCache(email: string | null | undefined): any | null {
  try {
    const k = empCacheKey(email);
    if (!k) return null;
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== EMP_CACHE_VERSION || !parsed.data) return null;
    return parsed.data;
  } catch { return null; }
}
function writeEmpCache(email: string | null | undefined, emp: any) {
  try {
    const k = empCacheKey(email);
    if (!k || !emp) return;
    localStorage.setItem(k, JSON.stringify({ v: EMP_CACHE_VERSION, data: emp }));
  } catch { /* storage disabled — best-effort */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [employeeLookup, setEmployeeLookup] = useState<EmployeeLookupStatus>('pending');
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [mfaState, setMfaState] = useState<'checking' | 'ok' | 'challenge'>('ok');
  const [requiresPasswordSetup, setRequiresPasswordSetup] =
    useState<'invite' | 'recovery' | null>(initialRequiresPasswordSetup);

  // Check for existing session on mount + subscribe to auth state changes.
  // The onAuthStateChange listener is critical: supabase-js auto-refreshes
  // JWTs every ~1 hour, but without this we'd keep the OLD (now-expired)
  // token in the apiAuth module. Any tier-gated edge endpoint hit after
  // the refresh would 401 with "employee record not found" because
  // /auth/v1/user rejects the expired token (Drew saw this on SPEC DETAILS
  // after a long session).
  useEffect(() => {
    checkSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔄 Auth state change:', event);
      // Fires on TOKEN_REFRESHED, SIGNED_IN, SIGNED_OUT, USER_UPDATED.
      // Mirror the React state + module-level apiAuth token so every
      // future fetch picks up the fresh JWT.
      if (session) {
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name,
        });
        setAccessToken(session.access_token);
        setApiAuthToken(session.access_token);
      } else {
        setUser(null);
        setEmployee(null);
        setAccessToken(null);
        setApiAuthToken(null);
      }
    });
    return () => { subscription.unsubscribe(); };
  }, []);

  const checkSession = async () => {
    try {
      console.log('🔐 Checking for existing session...');
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session check error:', error);
        setUser(null);
        setEmployee(null);
        setAccessToken(null);
      } else if (session) {
        console.log('✅ Found existing session for:', session.user.email);
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name,
        });
        setAccessToken(session.access_token);
        setApiAuthToken(session.access_token);
        if (session.user.email) {
          const cachedEmp = readEmpCache(session.user.email);
          if (cachedEmp) {
            // Fast path: render immediately from the cached employee record and
            // revalidate in the background. The auth gate no longer blocks on a
            // (possibly cold) /hr/employees round-trip for returning users.
            setEmployee(cachedEmp);
            void fetchEmployeeDetails(session.user.email, session.access_token);
          } else {
            // First sign-in on this device: resolve before render so the app
            // knows who the user is (currentUserId). One-time cost.
            await fetchEmployeeDetails(session.user.email, session.access_token);
          }
        }
      } else {
        console.log('ℹ️ No existing session found');
        setUser(null);
        setEmployee(null);
        setAccessToken(null);
        setApiAuthToken(null);
      }
    } catch (err) {
      console.error('Error checking session:', err);
      setUser(null);
      setEmployee(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (userEmail: string, token: string, retryCount = 0) => {
    try {
      console.log('👤 Fetching employee details for email:', userEmail, retryCount > 0 ? `(retry ${retryCount})` : '');

      // /hr/employees is tier-gated, so this MUST use the user's session
      // token (anon key returns 401 and the employee record is never found,
      // which downstream causes currentUserId to fall back to 'e1' and
      // attribute the user's edits to the wrong employee).
      const response = await fetch(`${BASE}/hr/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch employees:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Response body:', errorText);

        // Retry on 5xx errors
        if (response.status >= 500 && retryCount < 3) {
          console.log(`⏳ Retrying in ${(retryCount + 1) * 1000}ms...`);
          setTimeout(() => fetchEmployeeDetails(userEmail, token, retryCount + 1), (retryCount + 1) * 1000);
          return;
        }

        // The server could not be asked (503 while the database is down, a
        // 401 from a token mid-rotation). That is NOT "this person has no
        // employee row" — keep the record we already have. On 2026-09-15 the
        // database stalled, this branch nulled the cached employee, and the HR
        // module's old `?? 'e1'` fallback opened Stacy's profile for whoever
        // was signed in. A blank identity is the only safe answer here.
        setEmployeeLookup('unavailable');
        return;
      }

      const json = await response.json();
      console.log('📦 Received response:', json);
      const employeesData = json.employees || json.data || [];
      console.log('👥 Employee count:', employeesData.length);
      
      // Find employee by email (case-insensitive)
      const matchedEmployee = employeesData.find((emp: any) => 
        emp.email?.toLowerCase() === userEmail.toLowerCase()
      );

      if (matchedEmployee) {
        console.log('✅ Found employee record:', matchedEmployee.name);
        
        // Ensure snake_case fields are mapped to camelCase
        const mappedEmployee = {
          ...matchedEmployee,
          accessRole: matchedEmployee.access_role || matchedEmployee.accessRole || 'employee',
          accessTier: matchedEmployee.access_tier ?? (matchedEmployee.access_role === 'admin' ? 3 : matchedEmployee.access_role === 'manager' ? 2 : 1),
          // The access model's two fields. They arrive with the row, so the nav
          // resolves from the catalogue on the first paint — no round trip, no
          // flash of a menu the person is not allowed to see.
          accessProfile: matchedEmployee.access_profile ?? matchedEmployee.accessProfile ?? null,
          moneyLensOverride: matchedEmployee.money_lens_override ?? matchedEmployee.moneyLensOverride ?? null,
          employmentStatus: matchedEmployee.employment_status || matchedEmployee.employmentStatus || 'active',
        };
        
        setEmployee(mappedEmployee);
        setEmployeeLookup('ok');
        writeEmpCache(userEmail, mappedEmployee);
      } else {
        // The roster answered and they are not on it — the one definitive
        // "no employee" and the only branch allowed to clear the record.
        console.log('⚠️ No employee record found for email:', userEmail);
        setEmployee(null);
        setEmployeeLookup('missing');
      }
    } catch (err) {
      console.error('Error fetching employee details:', err);
      console.error('Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        type: err instanceof TypeError ? 'TypeError' : typeof err,
        BASE,
        publicAnonKey: publicAnonKey ? '✓ Available' : '✗ Missing',
        retryCount
      });
      
      // Retry on network errors
      if (err instanceof TypeError && err.message === 'Failed to fetch' && retryCount < 3) {
        console.log(`⏳ Network error, retrying in ${(retryCount + 1) * 1000}ms...`);
        setTimeout(() => fetchEmployeeDetails(userEmail, token, retryCount + 1), (retryCount + 1) * 1000);
        return;
      }

      // Same rule as the non-OK branch: a request that never got an answer
      // is not an answer. Keep the record; say the lookup is unavailable.
      setEmployeeLookup('unavailable');
    }
  };

  const retryEmployeeLookup = async () => {
    if (!user?.email || !accessToken) return;
    setEmployeeLookup('pending');
    await fetchEmployeeDetails(user.email, accessToken);
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔐 Attempting sign in for:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      throw new Error(error.message);
    }

    if (data.session) {
      console.log('✅ Sign in successful');
      setUser({
        id: data.user.id,
        email: data.user.email || '',
        name: data.user.user_metadata?.name,
      });
      setAccessToken(data.session.access_token);
      setApiAuthToken(data.session.access_token);
      if (data.user.email) {
        await fetchEmployeeDetails(data.user.email, data.session.access_token);
      }
    } else {
      throw new Error('No session returned from sign in');
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      console.error('Update password error:', error);
      throw new Error(error.message);
    }
    console.log('✅ Password updated');
  };

  const clearPasswordSetupRequirement = () => {
    setRequiresPasswordSetup(null);
  };

  const signOut = async () => {
    console.log('🚪 Signing out...');
    // scope:'local' — sign out THIS device only. The library default is
    // 'global', which revokes every session the account has (phone, second
    // browser, other desk). Prod auth.sessions showed those wipes: each one
    // orphaned the user's other devices, whose dead-token retries drained
    // the shared office-IP rate bucket and fed the re-sign-in storm.
    const { error } = await supabase.auth.signOut({ scope: 'local' });

    if (error) {
      console.error('Sign out error:', error);
      throw new Error(error.message);
    }

    console.log('✅ Sign out successful');
    // Clear any cached HR/profile data so a shared device never leaves one
    // worker's data at rest after they sign out.
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('mos:hrme:') || k.startsWith('mos:emp:'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* storage disabled — nothing to clear */ }
    // The refresh-restore snapshot goes too — the next sign-in (possibly a
    // different user on a shared device) starts at the default landing.
    clearLastLocation();
    setUser(null);
    setEmployee(null);
    setAccessToken(null);
    setApiAuthToken(null);
  };

  // Supabase hands back an aal1 session even when a verified factor exists —
  // enforcing the step-up is the application's job, so we ask on every session.
  const refreshMfaState = useCallback(async () => {
    if (!user) { setMfaState('ok'); return; }
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error || !data) { setMfaState('ok'); return; }
      setMfaState(
        data.nextLevel === 'aal2' && data.currentLevel !== 'aal2' ? 'challenge' : 'ok',
      );
    } catch {
      setMfaState('ok');
    }
  }, [user]);

  // Keyed on the user id so it runs once per signed-in identity: on restore,
  // after sign-in, and again after a sign-out/sign-in as somebody else.
  useEffect(() => {
    if (!user) { setMfaState('ok'); return; }
    let cancelled = false;
    setMfaState('checking');
    void (async () => {
      try {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        setMfaState(
          !error && data && data.nextLevel === 'aal2' && data.currentLevel !== 'aal2'
            ? 'challenge' : 'ok',
        );
      } catch {
        if (!cancelled) setMfaState('ok');
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // 115 files read this context. The value used to be a fresh object on
  // every render, and these five functions are redefined every render too,
  // so memoising the object alone would change nothing. Each is exposed as
  // a STABLE wrapper that calls the latest implementation (kept in a ref),
  // so the logic above is untouched and the value only changes when the
  // state in it does.
  const impl = useRef({ retryEmployeeLookup, signIn, signOut, clearPasswordSetupRequirement, updatePassword });
  impl.current = { retryEmployeeLookup, signIn, signOut, clearPasswordSetupRequirement, updatePassword };
  const stable = useMemo(() => ({
    retryEmployeeLookup: () => impl.current.retryEmployeeLookup(),
    signIn: (email: string, password: string) => impl.current.signIn(email, password),
    signOut: () => impl.current.signOut(),
    clearPasswordSetupRequirement: () => impl.current.clearPasswordSetupRequirement(),
    updatePassword: (newPassword: string) => impl.current.updatePassword(newPassword),
  }), []);
  const value = useMemo<AuthContextType>(() => ({
    user,
    employee,
    employeeLookup,
    loading,
    accessToken,
    requiresPasswordSetup,
    mfaState,
    refreshMfaState,
    ...stable,
  }), [user, employee, employeeLookup, loading, accessToken, requiresPasswordSetup, mfaState, refreshMfaState, stable]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}